import { useQuery } from '@tanstack/react-query';
import { ERC20_ABI } from '../config/oft';
import { STARGATE_USDC_POOLS } from '../config/stargate';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export interface StargateAllowanceData {
  allowance: bigint;
}

/**
 * Checks the USDC allowance for the StargateV2 pool contract.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 * @param address - Owner address to check allowance for
 */
export function useStargateAllowance(
  sourceChainId: number | undefined,
  address: `0x${string}` | undefined,
) {
  return useQuery<StargateAllowanceData>({
    queryKey: ['stargateAllowance', sourceChainId, address],
    queryFn: async () => {
      if (!sourceChainId || !address) {
        throw new Error('sourceChainId and address are required');
      }

      const poolConfig = STARGATE_USDC_POOLS[sourceChainId];
      if (!poolConfig) {
        throw new Error(`No Stargate USDC pool for chainId ${sourceChainId}`);
      }

      const publicClient = getSourceChainPublicClient(sourceChainId);

      const allowance = await publicClient.readContract({
        address: poolConfig.usdcAddress,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [address, poolConfig.poolAddress],
      });

      return { allowance };
    },
    enabled: !!sourceChainId && !!address,
    staleTime: 15_000,
    refetchInterval: 3_000,
  });
}
